import React from "react";
import { Icon } from "../core/Icon.jsx";

/** Gift-registry option — bank transfer, QR, envelope, or a store link. Cards align in a row: head, growing body, pinned foot. */
export function GiftCard({ icon = "gift", title, description, detail, detailLabel, qr, qrLabel = "Escanea para transferir", href, actionLabel, onAction, style }) {
  const [copied, setCopied] = React.useState(false);
  const [qrFailed, setQrFailed] = React.useState(false);
  const act = () => {
    if (detail && navigator.clipboard) navigator.clipboard.writeText(detail);
    if (onAction) onAction();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const downloadQr = async () => {
    if (!qr) return;
    try {
      const res = await fetch(qr);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "codigo-qr";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(qr, "_blank", "noopener");
    }
  };
  const actionStyle = {
    minHeight: 44, padding: "12px 24px", display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontFamily: "var(--font-caps)", fontSize: "var(--fs-micro)", letterSpacing: "var(--ls-widest)", textTransform: "uppercase",
    border: "1px solid color-mix(in srgb, var(--accent) 45%, transparent)",
    transition: "all var(--dur-fast) var(--ease-standard)",
  };
  return (
    <article style={{
      position: "relative", height: "100%", boxSizing: "border-box",
      display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
      padding: "var(--sp-8) var(--sp-5) var(--sp-6)",
      background: "var(--bg-elevated)",
      boxShadow: "0 2px 3px rgba(60,44,28,.03), 0 34px 60px -44px rgba(60,44,28,.42)",
      ...style,
    }}>
      <span style={{ position: "absolute", top: 0, left: "16%", right: "16%", height: 2, background: "linear-gradient(90deg, transparent, var(--accent) 30%, var(--gold-300) 50%, var(--accent) 70%, transparent)" }} />

      <span style={{
        width: 54, height: 54, borderRadius: "50%", display: "grid", placeItems: "center", flex: "none",
        background: "var(--accent-soft)", color: "var(--accent)",
        boxShadow: "0 0 0 1px color-mix(in srgb, var(--accent) 22%, transparent)",
      }}>
        <Icon name={icon} size={22} />
      </span>
      <h3 style={{ margin: "var(--sp-4) 0 0", fontFamily: "var(--font-flourish, var(--font-script))", fontWeight: 400, fontSize: "var(--fs-title)", lineHeight: 1.3, padding: "0 .1em .1em", color: "var(--fg-primary)" }}>{title}</h3>
      {description && (
        <p style={{ margin: "var(--sp-3) 0 0", maxWidth: 270, fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body-l)", lineHeight: "var(--lh-snug)", color: "var(--fg-secondary)", textWrap: "pretty" }}>{description}</p>
      )}

      <div style={{ flex: 1, minHeight: "var(--sp-5)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "var(--sp-4)", width: "100%", padding: "var(--sp-5) 0" }}>
        {detail && (
          <div style={{ display: "grid", justifyItems: "center", gap: 6 }}>
            {detailLabel && (
              <span style={{ fontFamily: "var(--font-caps)", fontSize: "var(--fs-micro)", letterSpacing: "var(--ls-wider)", textTransform: "uppercase", color: "var(--fg-muted)" }}>{detailLabel}</span>
            )}
            <span style={{
              fontFamily: "var(--font-caps)", fontSize: "var(--fs-body)", letterSpacing: "var(--ls-wide)", color: "var(--fg-primary)",
              padding: "9px 16px", background: "color-mix(in srgb, var(--accent-soft) 55%, transparent)",
              borderBottom: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)",
              userSelect: "all", wordBreak: "break-word",
            }}>{detail}</span>
          </div>
        )}
        {qr != null && (
          <div style={{ display: "grid", justifyItems: "center", gap: 8, maxWidth: "100%" }}>
            <span style={{
              display: "grid", placeItems: "center", padding: 8, boxSizing: "border-box",
              maxWidth: "100%",
              background: (qrFailed || !qr) ? "color-mix(in srgb, var(--accent-soft) 55%, transparent)" : "#fff",
              boxShadow: "0 0 0 1px color-mix(in srgb, var(--accent) 22%, transparent)",
              // Sin QR (placeholder): caja cuadrada fija. Con imagen real: se adapta al aspecto.
              ...((qrFailed || !qr) ? { width: 148, height: 148 } : {}),
            }}>
              {(qrFailed || !qr) ? (
                <span style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body)", lineHeight: "var(--lh-snug)", color: "var(--fg-muted)" }}>Sube aquí<br />tu código QR</span>
              ) : (
                <img
                  src={qr}
                  alt={qrLabel}
                  onError={() => setQrFailed(true)}
                  style={{ display: "block", maxWidth: "100%", width: "auto", height: "auto", maxHeight: 280, objectFit: "contain" }}
                />
              )}
            </span>
            <span style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "var(--fs-body)", color: "var(--fg-muted)" }}>{qrLabel}</span>
            {qr && !qrFailed && (
              <button type="button" onClick={downloadQr} style={{ ...actionStyle, color: "var(--fg-secondary)" }}>
                Descargar imagen
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ display: "grid", justifyItems: "center", gap: "var(--sp-4)", width: "100%" }}>
        {actionLabel && (href ? (
          <a href={href} target="_blank" rel="noreferrer" style={{ ...actionStyle, color: "var(--fg-secondary)" }}>{actionLabel}</a>
        ) : (
          <button type="button" onClick={act} style={{
            ...actionStyle,
            color: copied ? "var(--accent)" : "var(--fg-secondary)",
            background: copied ? "var(--accent-soft)" : "transparent",
          }}>{copied ? "Copiado" : actionLabel}</button>
        ))}
        <span style={{ color: "color-mix(in srgb, var(--accent) 55%, transparent)", fontSize: 7, letterSpacing: "0.6em" }}>◆◆◆</span>
      </div>
    </article>
  );
}
